import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';
import { NavLink } from 'react-router-dom';

// The checked segment reads as raised out of the track, so in dark mode the
// track drops to the card level and the segment sits a step above it -- the
// reverse of the ramp, which would put the lighter color underneath.
const TRACK_CLASS = 'gap-0.5 rounded-lg bg-neutral-100 p-0.5 dark:bg-neutral-800';

const SEGMENT_CLASS = clsx(
  'group flex min-w-0 items-center justify-center gap-1.5 rounded-md px-2 py-1.5',
  'text-sm/5 text-neutral-600 dark:text-neutral-400 forced-colors:outline'
);

/**
 * A one-of-N choice rendered as adjacent segments in a shared trough, for
 * options whose labels are short enough to read side by side.
 *
 * Built on Headless RadioGroup so keyboard and screen-reader behavior matches
 * `radio.tsx`; the difference is presentational. Prefer `RadioGroup` when the
 * options need descriptions, or when there are more than four — segments divide
 * the available width evenly and stop being readable.
 */
export function SegmentedControl<T>({
  className,
  ...props
}: { className?: string } & Omit<Headless.RadioGroupProps<'div', T>, 'className'>) {
  return (
    <Headless.RadioGroup
      data-slot="control"
      {...props}
      className={clsx('flex w-full', TRACK_CLASS, className)}
    />
  );
}

export function SegmentedControlOption<T>({
  className,
  icon: Icon,
  children,
  ...props
}: {
  className?: string;
  /** Leading glyph, tinted to match the label so a segment never reads half-active. */
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
} & Omit<Headless.RadioProps<'button', T>, 'className' | 'children'>) {
  return (
    <Headless.Radio
      as="button"
      type="button"
      {...props}
      className={clsx(
        SEGMENT_CLASS,
        'flex-1',
        'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-1 data-[focus]:outline-primary-500',
        'data-[hover]:text-neutral-900 dark:data-[hover]:text-white',
        'data-[checked]:bg-white data-[checked]:font-medium data-[checked]:text-neutral-950 data-[checked]:shadow-sm',
        'dark:data-[checked]:bg-neutral-600 dark:data-[checked]:text-white',
        'data-[disabled]:opacity-50',
        className
      )}
    >
      {Icon && (
        <Icon className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500 group-data-[checked]:text-neutral-500 dark:group-data-[checked]:text-neutral-400" />
      )}
      <span className="truncate">{children}</span>
    </Headless.Radio>
  );
}

export interface SegmentedNavigationRoute {
  path: string;
  title: string;
  hidden?: boolean;
}

/**
 * The segmented control as a tab strip for child routes. Links rather than
 * radios, so each segment is a URL that can be opened, bookmarked and reached
 * with Back. Sized to its labels rather than stretched across the row, and
 * scrolls sideways on a screen too narrow for every label.
 */
export const SegmentedNavigation = ({
  routes,
  className,
  'aria-label': ariaLabel = 'Tabs'
}: {
  routes: SegmentedNavigationRoute[];
  className?: string;
  'aria-label'?: string;
}) => (
  <div className={clsx('max-w-full overflow-x-auto', className)}>
    <nav aria-label={ariaLabel} className={clsx('inline-flex', TRACK_CLASS)}>
      {routes
        .filter((route) => !route.hidden)
        .map((route) => (
          <NavLink
            key={route.path}
            to={route.path}
            className={clsx(
              SEGMENT_CLASS,
              'shrink-0 whitespace-nowrap px-3',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500',
              'hover:text-neutral-900 dark:hover:text-white',
              'aria-[current=page]:bg-white aria-[current=page]:font-medium aria-[current=page]:text-neutral-950 aria-[current=page]:shadow-sm',
              'dark:aria-[current=page]:bg-neutral-600 dark:aria-[current=page]:text-white'
            )}
          >
            {route.title}
          </NavLink>
        ))}
    </nav>
  </div>
);
